'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Star, Send, Loader2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { submitLeadFeedbackAction, type LeadFeedbackData } from '@/app/actions/quote';
import { trackEvent } from '@/lib/analytics';
import NavigationLinks from './NavigationLinks';

interface ConsumerSuccessPageProps {
	leadId?: string;
	contactName?: string;
	onFeedbackSent?: () => void;
}

const REFERRAL_OPTIONS = [
	{ value: 'google', label: 'Google zoeken' },
	{ value: 'ai_tool', label: 'Via een AI-tool (ChatGPT, etc.)' },
	{ value: 'social_media', label: 'Social media (Facebook, Instagram)' },
	{ value: 'recommendation', label: 'Aanbeveling van bekende' },
	{ value: 'installer', label: 'Via een monteur/installateur' },
	{ value: 'other', label: 'Anders' },
];

export default function ConsumerSuccessPage({ leadId, contactName, onFeedbackSent }: ConsumerSuccessPageProps) {
	const [referralSource, setReferralSource] = useState<string | null>(null);
	const [referralOther, setReferralOther] = useState('');
	const [rating, setRating] = useState(0);
	const [hoveredStar, setHoveredStar] = useState(0);
	const [feedbackText, setFeedbackText] = useState('');
	const [isSending, setIsSending] = useState(false);
	const [feedbackSent, setFeedbackSent] = useState(false);

	const firstName = contactName?.split(' ')[0] || '';

	const handleSubmitFeedback = async () => {
		if (!leadId) return;
		if (!referralSource && rating === 0) return;

		setIsSending(true);

		const feedback: LeadFeedbackData = {};
		if (referralSource) {
			feedback.referral_source = referralSource;
			if (referralSource === 'other' && referralOther.trim()) {
				feedback.referral_source_other = referralOther.trim();
			}
		}
		if (rating > 0) {
			feedback.website_rating = rating;
		}
		if (feedbackText.trim()) {
			feedback.website_feedback = feedbackText.trim();
		}

		const result = await submitLeadFeedbackAction(leadId, feedback);

		if (result.success) {
			trackEvent('feedback_submitted', {
				form_name: 'particulier_offerte',
				referral_source: referralSource || 'none',
				website_rating: rating,
			});
			if (onFeedbackSent) {
				onFeedbackSent();
			} else {
				setFeedbackSent(true);
			}
		}

		setIsSending(false);
	};

	return (
		<div className="w-full">
			{/* Success Header */}
			<div className="w-full bg-blue-dark py-10 md:py-14">
				<div className="max-w-2xl mx-auto px-4 text-center">
					<motion.div
						initial={{ scale: 0.5, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ duration: 0.5, type: 'spring' }}
						className="flex justify-center mb-5"
					>
						<div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
							<CheckCircle className="size-14 md:size-16 text-white" />
						</div>
					</motion.div>

					<motion.h2
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3, duration: 0.4 }}
						className="text-3xl md:text-4xl font-bold font-heading text-white mb-4"
					>
						{firstName ? `Bedankt, ${firstName}!` : 'Aanvraag Ontvangen!'}
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.4, duration: 0.4 }}
						className="text-blue-100 text-lg md:text-xl max-w-lg mx-auto leading-relaxed"
					>
						We hebben uw aanvraag in goede orde ontvangen en nemen
						<span className="font-semibold text-white"> binnen enkele werkdagen </span>
						contact met u op.
					</motion.p>
				</div>
			</div>

			{/* Feedback Section */}
			<div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
				{leadId && !feedbackSent && (
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.6, duration: 0.5 }}
						className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10"
					>
						<div className="mb-8">
							<h3 className="text-xl md:text-2xl font-heading font-semibold text-blue-dark mb-2">
								Mogen we u iets vragen?
							</h3>
							<p className="text-base text-gray-500">
								Geheel optioneel — uw antwoorden helpen ons verbeteren.
							</p>
						</div>

						{/* Referral Source */}
						<div className="mb-10">
							<label className="block text-base font-semibold text-blue-dark mb-4">
								Hoe heeft u ons gevonden?
							</label>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								{REFERRAL_OPTIONS.map((option) => {
									const isSelected = referralSource === option.value;

									return (
										<button
											key={option.value}
											type="button"
											onClick={() => setReferralSource(option.value)}
											className={`flex items-center gap-3 text-left px-5 py-4 rounded-xl border text-base font-medium transition-all duration-200 cursor-pointer ${
												isSelected
													? 'border-blue-dark bg-blue-light text-blue-dark ring-1 ring-blue-dark/20'
													: 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
											}`}
										>
											<span
												className={`flex-shrink-0 size-5 rounded-full border-2 flex items-center justify-center transition-colors ${
													isSelected ? 'border-blue-dark' : 'border-gray-300'
												}`}
											>
												{isSelected && <span className="size-2.5 rounded-full bg-blue-dark" />}
											</span>
											{option.label}
										</button>
									);
								})}
							</div>

							{/* "Anders" text field */}
							{referralSource === 'other' && (
								<motion.div
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: 'auto' }}
									transition={{ duration: 0.2 }}
									className="mt-4"
								>
									<input
										type="text"
										value={referralOther}
										onChange={(e) => setReferralOther(e.target.value)}
										placeholder="Via welk kanaal?"
										className="w-full px-5 py-3 rounded-xl border border-gray-200 text-base focus:outline-none focus:ring-2 focus:ring-blue-dark/20 focus:border-blue-dark transition-colors"
										id="referral_source_other"
									/>
								</motion.div>
							)}
						</div>

						{/* Website Rating */}
						<div className="mb-10">
							<label className="block text-base font-semibold text-blue-dark mb-4">
								Hoe beoordeelt u onze website?
							</label>
							<div className="flex flex-col items-start gap-2">
								<div className="flex items-center gap-1 sm:gap-2">
									{[1, 2, 3, 4, 5].map((star) => (
										<button
											key={star}
											type="button"
											onClick={() => setRating(star)}
											onMouseEnter={() => setHoveredStar(star)}
											onMouseLeave={() => setHoveredStar(0)}
											className="p-1 sm:p-1.5 transition-transform hover:scale-110"
											aria-label={`${star} ster${star > 1 ? 'ren' : ''}`}
										>
											<Star
												className={`size-8 sm:size-10 md:size-11 transition-colors duration-150 ${
													star <= (hoveredStar || rating)
														? 'text-orange fill-orange'
														: 'text-gray-300'
												}`}
											/>
										</button>
									))}
								</div>
								{rating > 0 && (
									<span className="text-base text-gray-500 font-medium">
										{rating === 1 && 'Matig'}
										{rating === 2 && 'Kan beter'}
										{rating === 3 && 'Goed'}
										{rating === 4 && 'Heel goed'}
										{rating === 5 && 'Uitstekend!'}
									</span>
								)}
							</div>
						</div>

						{/* Optional feedback text */}
						<div className="mb-8">
							<label
								htmlFor="website_feedback"
								className="block text-base font-semibold text-blue-dark mb-3"
							>
								Heeft u nog opmerkingen of suggesties?{' '}
								<span className="text-gray-400 font-normal">(optioneel)</span>
							</label>
							<textarea
								id="website_feedback"
								value={feedbackText}
								onChange={(e) => setFeedbackText(e.target.value)}
								rows={3}
								className="w-full px-5 py-4 rounded-xl border border-gray-200 text-base focus:outline-none focus:ring-2 focus:ring-blue-dark/20 focus:border-blue-dark transition-colors resize-none"
								placeholder="Uw feedback wordt gewaardeerd..."
							/>
						</div>

						{/* Submit Feedback */}
						<Button
							type="button"
							onClick={handleSubmitFeedback}
							disabled={isSending || (!referralSource && rating === 0)}
							className="bg-orange hover:bg-orange-hover text-white font-bold text-lg py-6 px-10 w-full md:w-auto min-w-[220px]"
						>
							{isSending ? (
								<>
									<Loader2 className="mr-2 size-5 animate-spin" /> Verzenden...
								</>
							) : (
								<>
									<Send className="mr-2 size-5" /> Verstuur Feedback
								</>
							)}
						</Button>
					</motion.div>
				)}

				{/* Feedback Sent Confirmation */}
				{feedbackSent && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.3 }}
						className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10 text-center"
					>
						<div className="flex justify-center mb-4">
							<div className="bg-seaweed/10 p-4 rounded-full">
								<Heart className="size-10 text-seaweed" />
							</div>
						</div>
						<h3 className="text-xl md:text-2xl font-heading font-semibold text-blue-dark mb-2">
							Bedankt voor uw feedback!
						</h3>
						<p className="text-base text-gray-500">
							Uw mening helpt ons om onze service te verbeteren.
						</p>
					</motion.div>
				)}
			</div>

			{/* Navigation */}
			<NavigationLinks />
		</div>
	);
}
